import { useLocales } from '../../../hooks/useLanguages';
import { useState, useMemo, useEffect, useCallback } from 'react';
import LanguageModal from './LanguageModal';
import { useTranslation } from '../../../hooks/useTranslation';

const LocalesTab = () => {
  const {t} = useTranslation();
  
  // Filter states
  const [filters, setFilters] = useState({
    is_active: null,
    code: '',
    name: ''
  });

  // Debounced filter states for API calls
  const [debouncedFilters, setDebouncedFilters] = useState({
    is_active: null,
    code: '',
    name: ''
  });

  // Debounce effect for text filters (code and name)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(prev => ({
        ...prev,
        code: filters.code,
        name: filters.name
      }));
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [filters.code, filters.name]);

  // Immediate update for dropdown filter (is_active)
  useEffect(() => {
    setDebouncedFilters(prev => ({
      ...prev,
      is_active: filters.is_active
    }));
  }, [filters.is_active]);

  // Prepare query parameters for API request using debounced filters
  const queryParams = useMemo(() => {
    const params = {};
    
    if (debouncedFilters.is_active !== null) {
      params.is_active = debouncedFilters.is_active;
    }
    
    if (debouncedFilters.code !== '') {
      params.code = debouncedFilters.code;
    }
    
    if (debouncedFilters.name !== '') {
      params.name = debouncedFilters.name;
    }
    
    return params;
  }, [debouncedFilters]);

  // Fetch locales with filters
  const { data: locales, isLoading, refetch } = useLocales(queryParams);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState(null);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilter = (key) => {
    setFilters(prev => ({
      ...prev,
      [key]: key === 'is_active' ? null : ''
    }));
  };

  return (
    <div className="language-management__content">
      <div className="language-management__filters">
        <div className="language-management__filters-left">
          <div className="language-management__filter-group">
            <label className="language-management__filter-label">
              {t('locales.status')}
            </label>
            <div className="language-management__filter-input-wrapper">
              <select
                className="language-management__filter-select"
                value={filters.is_active === null ? '' : filters.is_active.toString()}
                onChange={(e) => handleFilterChange('is_active', e.target.value === '' ? null : e.target.value === 'true')}
              >
                <option value="">{t('locales.allStatuses')}</option>
                <option value="true">{t('locales.active')}</option>
                <option value="false">{t('locales.inactive')}</option>
              </select>
              {filters.is_active !== null && (
                <button
                  className="language-management__filter-clear-btn"
                  onClick={() => clearFilter('is_active')}
                  title={t('locales.clearStatusFilter')}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          
          <div className="language-management__filter-group">
            <label className="language-management__filter-label">
              {t('locales.languageCode')}
            </label>
            <div className="language-management__filter-input-wrapper">
              <input
                type="text"
                className="language-management__filter-input"
                placeholder={t('locales.languageCodePlaceholder')}
                value={filters.code}
                onChange={(e) => handleFilterChange('code', e.target.value)}
              />
              {filters.code !== '' && (
                <button
                  className="language-management__filter-clear-btn"
                  onClick={() => clearFilter('code')}
                  title={t('locales.clearCodeFilter')}
                >
                  ×
                </button>
              )}
              {filters.code !== debouncedFilters.code && (
                <div className="language-management__debounce-indicator" title={t('locales.filteringInProgress')}>
                  <div className="language-management__debounce-spinner"></div>
                </div>
              )}
            </div>
          </div>
          
          <div className="language-management__filter-group">
            <label className="language-management__filter-label">
              {t('locales.languageName')}
            </label>
            <div className="language-management__filter-input-wrapper">
              <input
                type="text"
                className="language-management__filter-input"
                placeholder={t('locales.languageNamePlaceholder')}
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
              />
              {filters.name !== '' && (
                <button
                  className="language-management__filter-clear-btn"
                  onClick={() => clearFilter('name')}
                  title={t('locales.clearNameFilter')}
                >
                  ×
                </button>
              )}
              {filters.name !== debouncedFilters.name && (
                <div className="language-management__debounce-indicator" title={t('locales.filteringInProgress')}>
                  <div className="language-management__debounce-spinner"></div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="language-management__add-language">
            <button 
              className="language-management__add-btn"
              onClick={() => setIsModalOpen(true)}
            >
              <span className="language-management__add-icon">+</span>
              {t('locales.addLanguage')}
            </button>
          </div>
      </div>
      
      <div className="language-management__table">
        {isLoading && (
          <div className="language-management__loading">
            <div className="language-management__loading-spinner"></div>
            <span>{t('locales.loading')}</span>
          </div>
        )}
        
        {!isLoading && (
          <>
            <div className="language-management__table-header">
              <div className="language-management__table-cell" data-label="Language">{t('locales.tableHeaders.language')}</div>
              <div className="language-management__table-cell" data-label="Code">{t('locales.tableHeaders.code')}</div>
              <div className="language-management__table-cell" data-label="Status">{t('locales.tableHeaders.status')}</div>
              <div className="language-management__table-cell" data-label="Actions">{t('locales.tableHeaders.actions')}</div>
            </div>
            {locales?.map((lang) => (
              <div key={lang.code} className="language-management__table-row">
                <div className="language-management__table-cell" data-label="Language">
                  <div className="language-management__lang-info">
                    <span className="language-management__name">{lang.name}</span>
                  </div>
                </div>
                <div className="language-management__table-cell" data-label="Code">
                  <code>{lang.code}</code>
                </div>
                <div className="language-management__table-cell" data-label="Status">
                  <span className={`language-management__status ${lang.is_active ? 'active' : 'inactive'}`}>
                    {lang.is_active ? t('locales.active') : t('locales.inactive')}
                  </span>
                </div>
                <div className="language-management__table-cell" data-label="Actions">
                  <div className="language-management__actions">
                    <button
                      className="language-management__action-btn edit"
                      onClick={() => {
                        setIsModalOpen(true);
                        setEditingLanguage(lang);
                      }}
                      title={t('locales.editLanguage')}
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Language Modal */}
      <LanguageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingLanguage={editingLanguage}
        setEditingLanguage={setEditingLanguage}
        refetch={refetch}
      />
    </div>
  )
}

export default LocalesTab